/* eslint-disable @typescript-eslint/no-explicit-any */
import {
  ButtonERP,
  ButtonFilterERP,
  CalendarPrimeReact,
  InputCurrency,
  OptionsMenuStorybook,
  RadioButtonERP,
  SearchInput,
} from "@finanedi.es/storybook"
import { faDownload } from "@fortawesome/pro-light-svg-icons"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { GetMovimientosTarjetaBancariaByFiltersDtoIPagedEnumerable } from "api/Schemas/GetMovimientosTarjetaBancariaByFiltersDtoIPagedEnumerable"
import { GetMovimientosTarjetaBancariaQueryParamsProps } from "api/TarjetaBancaria.service"
import { TIPO_TRANSACCION } from "api/Types/TipoTransaccionEnum"
import { formatNumber } from "common/utils/currency"
import { DATE_YYYY_MM_DD } from "constants/formatoFechas"
import { format } from "date-fns"
import { AnimatePresence } from "framer-motion"
import { isNaN } from "lodash"
import { Dialog } from "primereact/dialog"
import { Dispatch, useEffect, useState } from "react"
import { useTranslation } from "react-i18next"
import { tarjetaDetailFiltersProps } from "./contants"

export const FilterMenu = ({
  serviceQuery,
  sortingCriteria,
  skipCount,
  maxResultCount,
  resetPagination,
  bankDetailsFilter,
  setBankDetailsFilter,
  exportToExcel,
}: {
  serviceQuery: Dispatch<
    Partial<GetMovimientosTarjetaBancariaByFiltersDtoIPagedEnumerable>
  >
  sortingCriteria?: GetMovimientosTarjetaBancariaQueryParamsProps["SortingCriteria"]
  skipCount?: GetMovimientosTarjetaBancariaQueryParamsProps["SkipCount"]
  maxResultCount?: GetMovimientosTarjetaBancariaQueryParamsProps["MaxResultCount"]
  resetPagination?: () => void
  bankDetailsFilter: tarjetaDetailFiltersProps
  setBankDetailsFilter: React.Dispatch<
    React.SetStateAction<tarjetaDetailFiltersProps>
  >
  exportToExcel: () => void
}) => {
  const {
    t,
    i18n: { language },
  } = useTranslation()
  const [menuOpen, setMenuOpen] = useState(false)
  const handleSetBankDetailsFilter = <
    K extends keyof tarjetaDetailFiltersProps
  >(
    key: K,
    value: tarjetaDetailFiltersProps[K]
  ) => {
    const updateFilter = {
      ...bankDetailsFilter,
      [key]: value,
    }
    setBankDetailsFilter(updateFilter)
  }

  const openFilters = () => {
    setMenuOpen(true)
  }
  const queryFixer = (
    queryParams: Partial<GetMovimientosTarjetaBancariaQueryParamsProps>
  ) => {
    const movimientosTarjetaDetailParams = {
      Id: queryParams.Id,
      AmountUpperLimit: queryParams.AmountUpperLimit,
      AmountLowerLimit: queryParams.AmountLowerLimit,
      BookingDateLowerLimit: queryParams.BookingDateLowerLimit,
      BookingDateUpperLimit: queryParams.BookingDateUpperLimit,
      Keyword: queryParams.Keyword,
      Type: queryParams.Type,
      SortingCriteria:
        queryParams.SortingCriteria?.property &&
        queryParams.SortingCriteria?.sort
          ? `${queryParams.SortingCriteria.property} ${queryParams.SortingCriteria.sort}`
          : "BookingDate DESC",
      MaxResultCount: queryParams.MaxResultCount,
      SkipCount: queryParams.SkipCount,
    }
    const fixedQueryParams = Object.fromEntries(
      Object.entries(movimientosTarjetaDetailParams).filter(
        ([_key, value]) => value || value === 0 // eslint-disable-line @typescript-eslint/no-unused-vars
      )
    )
    return fixedQueryParams
  }
  const query = {
    Id: bankDetailsFilter.id,
    AmountLowerLimit: bankDetailsFilter.amount?.min,
    AmountUpperLimit: bankDetailsFilter.amount?.max,
    BookingDateLowerLimit: bankDetailsFilter.date?.min
      ? format(new Date(bankDetailsFilter.date?.min), DATE_YYYY_MM_DD)
      : undefined,
    BookingDateUpperLimit: bankDetailsFilter.date?.max
      ? format(new Date(bankDetailsFilter.date?.max), DATE_YYYY_MM_DD)
      : undefined,
    Keyword: bankDetailsFilter.keyword,
    Type: bankDetailsFilter.movementType,
    SortingCriteria: sortingCriteria,
    SkipCount: skipCount,
    MaxResultCount: maxResultCount,
  }
  const handleQuery = () => {
    serviceQuery(queryFixer(query))
  }
  const handleApplyFilters = () => {
    setMenuOpen(false)
  }
  const hideFilter = () => {
    setMenuOpen(!menuOpen)
  }
  const handleResetFilters = () => {
    const updateFilters = {
      ...bankDetailsFilter,
      date: { min: undefined, max: undefined },
      amount: { min: undefined, max: undefined },
      movementType: undefined,
    }
    setBankDetailsFilter(updateFilters)
  }
  const appliedFiltersTags = [
    {
      label: t("bancos.cardsTable.detail.filterMenu.amountTypeLabel"),
      value:
        bankDetailsFilter.movementType === TIPO_TRANSACCION.INGRESOS
          ? t("bancos.cardsTable.detail.filterMenu.incomesLabel")
          : undefined,
      action: () => {
        handleSetBankDetailsFilter("movementType", undefined)
      },
    },
    {
      label: t("bancos.cardsTable.detail.filterMenu.amountTypeLabel"),
      value:
        bankDetailsFilter.movementType === TIPO_TRANSACCION.GASTOS
          ? t("bancos.cardsTable.detail.filterMenu.expensesLabel")
          : undefined,
      action: () => {
        handleSetBankDetailsFilter("movementType", undefined)
      },
    },
    {
      label: t("bancos.cardsTable.detail.table.filter.amountFrom"),
      value:
        bankDetailsFilter.amount?.min !== null &&
        bankDetailsFilter.amount?.min !== undefined
          ? Intl.NumberFormat("de-DE", {
              style: "currency",
              currency: "EUR",
            }).format(bankDetailsFilter.amount?.min)
          : undefined,
      action: () => {
        handleSetBankDetailsFilter("amount", {
          ...bankDetailsFilter.amount,
          min: undefined,
        })
      },
    },
    {
      label: t("bancos.cardsTable.detail.table.filter.amountTo"),
      value:
        bankDetailsFilter.amount?.max !== null &&
        bankDetailsFilter.amount?.max !== undefined
          ? Intl.NumberFormat("de-DE", {
              style: "currency",
              currency: "EUR",
            }).format(bankDetailsFilter.amount?.max)
          : undefined,
      action: () => {
        handleSetBankDetailsFilter("amount", {
          ...bankDetailsFilter.amount,
          max: undefined,
        })
      },
    },
    {
      label: t("bancos.cardsTable.detail.table.filter.dateFrom"),
      value: bankDetailsFilter.date?.min
        ? Intl.DateTimeFormat("es-ES", {
            year: "numeric",
            month: "2-digit",
            day: "2-digit",
          }).format(new Date(bankDetailsFilter.date.min))
        : undefined,
      action: () => {
        handleSetBankDetailsFilter("date", {
          ...bankDetailsFilter.date,
          min: undefined,
        })
      },
    },
    {
      label: t("bancos.cardsTable.detail.table.filter.dateTo"),
      value: bankDetailsFilter.date?.max
        ? Intl.DateTimeFormat("es-ES", {
            year: "numeric",
            month: "2-digit",
            day: "2-digit",
          }).format(new Date(bankDetailsFilter.date.max))
        : undefined,
      action: () => {
        handleSetBankDetailsFilter("date", {
          ...bankDetailsFilter.date,
          max: undefined,
        })
      },
    },
  ]
  const appliedFilters = appliedFiltersTags.filter((filter) => filter.value)

  useEffect(() => {
    resetPagination?.()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bankDetailsFilter.keyword, appliedFilters?.length])
  useEffect(() => {
    handleQuery()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    sortingCriteria?.sort,
    sortingCriteria?.property,
    bankDetailsFilter?.keyword,
    bankDetailsFilter.amount?.min,
    bankDetailsFilter.amount?.max,
    bankDetailsFilter.date?.min,
    bankDetailsFilter.date?.max,
    bankDetailsFilter.movementType,
    skipCount,
    maxResultCount,
  ])

  return (
    <>
      <div className='!tw-px-6 tw-py-4 tw-mt-1 tw-flex tw-flex-col md:tw-flex-row tw-justify-between tw-items-center tw-shadow-3xl tw-rounded-t-xl tw-mb-px tw-bg-white'>
        <p className='tw-whitespace-nowrap tw-text-lg tw-font-medium tw-text-text tw-my-auto'>
          {t("bancos.cardsTable.idView.labelMovements")}
        </p>
        <div className='tw-flex tw-justify-center lg:tw-justify-end'>
          <div className='!tw-w-fit tw-flex tw-items-center !tw-flex-row tw-justify-end tw-flex-wrap'>
            <div className='tw-grid tw-gap-1 lg:tw-gap-3 sm:tw-flex sm:tw-flex-row tw-items-center tw-w-fit'>
              <SearchInput
                placeholder={`${t("bancos.searchPlaceholder")}`}
                onChange={(ev: string) => {
                  resetPagination?.()
                  handleSetBankDetailsFilter("keyword", ev)
                }}
                inputValue={bankDetailsFilter.keyword || ""}
                className='tw-h-10 tw-w-full sm:tw-w-72'
              />
              <ButtonFilterERP
                isActive={appliedFilters.length > 0}
                numberActive={appliedFilters?.length}
                onClick={openFilters}
                deleteFilter={handleResetFilters}
                filterButtonLabel={`${t("filtros.title")}`}
              />
              <OptionsMenuStorybook
                items={[
                  {
                    title: `${t("bancos.idView.labelDownload")}`,
                    hasRedirect: false,
                    icon: faDownload,
                    className: "tw-text-text-50",
                    action: exportToExcel,
                  },
                ]}
                position='right'
              />
            </div>
          </div>
        </div>
      </div>

      <AnimatePresence>
        <Dialog
          breakpoints={{ "575px": "100vw" }}
          className='!tw-m-0 !tw-h-full tw-select-none'
          header={t("filtros.title")}
          visible={menuOpen}
          footer={
            <div className='tw-w-full tw-border-t-2 tw-pt-6'>
              <ButtonERP
                customize={{
                  type: "primary",
                  size: "md",
                }}
                className='!tw-w-full'
                onClick={handleApplyFilters}
                label={`${t("filtros.labelApply")}`}
              />
            </div>
          }
          onHide={() => {
            hideFilter()
          }}
          draggable={false}
          resizable={false}
          position='right'
          closable
          dismissableMask
          closeOnEscape
        >
          {!!appliedFilters?.length && (
            <div className='tw-mb-8'>
              <div className='tw-flex tw-mb-4 tw-justify-between'>
                <span className='tw-font-medium'>
                  {t("bancos.cardsTable.detail.table.appliedFilters")}
                </span>
                <span
                  onClick={handleResetFilters}
                  className='tw-text-text-hyperlinks tw-cursor-pointer'
                >
                  {t("bancos.cardsTable.detail.table.filter.deleteAll")}
                </span>
              </div>
              <div className='tw-flex tw-flex-wrap tw-mb-8 tw-gap-2 tw-font-normal tw-text-xs'>
                {appliedFilters.map((filter) => (
                  <div
                    key={filter.label}
                    onClick={filter.action}
                    className='tw-items-center tw-justify-between tw-cursor-pointer tw-bg-info-10 tw-text-info tw-rounded-md tw-border tw-border-info tw-py-1 tw-px-2'
                  >
                    <span>
                      {filter.label} {filter.value}
                    </span>
                    <FontAwesomeIcon
                      icon={["fal", "xmark"]}
                      className='tw-ml-2 tw-text-info'
                    />
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className='tw-mb-8'>
            <div className='tw-mb-4'>
              <span className='tw-font-medium'>
                {t("bancos.cardsTable.detail.filterMenu.amountTypeTitle")}
              </span>
            </div>
            <div className='tw-grid tw-gap-4'>
              <div className='tw-flex tw-items-center'>
                <RadioButtonERP
                  inputId='ingresos'
                  name='movementType'
                  value={TIPO_TRANSACCION.INGRESOS}
                  onChange={() =>
                    bankDetailsFilter.movementType === TIPO_TRANSACCION.INGRESOS
                      ? handleSetBankDetailsFilter("movementType", undefined)
                      : handleSetBankDetailsFilter(
                          "movementType",
                          TIPO_TRANSACCION.INGRESOS
                        )
                  }
                  checked={
                    bankDetailsFilter.movementType === TIPO_TRANSACCION.INGRESOS
                  }
                  unstyled
                />
                <label
                  htmlFor='ingresos'
                  className='tw-cursor-pointer tw-capitalize tw-mb-0 tw-ml-3'
                >
                  {t("bancos.conciliacion.filterMenu.incomesLabel")}
                </label>
              </div>
              <div className='tw-flex tw-items-center'>
                <RadioButtonERP
                  inputId='gastos'
                  name='movementType'
                  value={TIPO_TRANSACCION.GASTOS}
                  onChange={() =>
                    bankDetailsFilter.movementType === TIPO_TRANSACCION.GASTOS
                      ? handleSetBankDetailsFilter("movementType", undefined)
                      : handleSetBankDetailsFilter(
                          "movementType",
                          TIPO_TRANSACCION.GASTOS
                        )
                  }
                  checked={
                    bankDetailsFilter.movementType === TIPO_TRANSACCION.GASTOS
                  }
                  unstyled
                />
                <label
                  htmlFor='gastos'
                  className='tw-cursor-pointer tw-capitalize tw-mb-0 tw-ml-3'
                >
                  {t("bancos.conciliacion.filterMenu.expensesLabel")}
                </label>
              </div>
            </div>
          </div>

          <div className='tw-mb-8'>
            <div className='tw-mb-4'>
              <span className='tw-font-medium'>
                {t("bancos.cardsTable.detail.filterMenu.amountTitle")}
              </span>
            </div>
            <div className='tw-flex tw-flex-row tw-mb-8 tw-justify-between tw-items-end'>
              <div className='tw-flex tw-flex-col'>
                <label>
                  {t("bancos.cardsTable.detail.filterMenu.amountLabelSince")}
                </label>
                <InputCurrency
                  placeholder='0,00'
                  value={bankDetailsFilter.amount?.min}
                  onChange={(value) =>
                    handleSetBankDetailsFilter("amount", {
                      ...bankDetailsFilter.amount,
                      min: isNaN(value) ? undefined : value,
                    })
                  }
                  min={Number.MIN_SAFE_INTEGER}
                  max={bankDetailsFilter.amount?.max ?? Number.MAX_SAFE_INTEGER}
                />
              </div>
              <div className='tw-px-3 tw-py-3 tw-items-baseline tw-w-fit'>
                -
              </div>
              <div className='tw-flex tw-flex-col'>
                <label>
                  {t("bancos.cardsTable.detail.filterMenu.amountLabelUntil")}
                </label>
                <InputCurrency
                  placeholder={
                    bankDetailsFilter.amount?.min
                      ? `${formatNumber(bankDetailsFilter.amount?.min)}`
                      : "0,00"
                  }
                  value={bankDetailsFilter.amount?.max}
                  onChange={(value) =>
                    handleSetBankDetailsFilter("amount", {
                      ...bankDetailsFilter.amount,
                      max: isNaN(value) ? undefined : value,
                    })
                  }
                  min={bankDetailsFilter.amount?.min ?? Number.MIN_SAFE_INTEGER}
                  max={Number.MAX_SAFE_INTEGER}
                />
              </div>
            </div>
          </div>

          <div className='tw-mb-8'>
            <div className='tw-mb-4'>
              <span className='tw-font-medium'>
                {t("bancos.cardsTable.detail.filterMenu.operationDate")}
              </span>
            </div>
            <div className='tw-flex tw-flex-row tw-mb-6'>
              <div className='tw-flex tw-flex-col'>
                <label>
                  {t("bancos.cardsTable.detail.filterMenu.dateLabelSince")}
                </label>
                <CalendarPrimeReact
                  id='fechaDesde'
                  name='fechaDesde'
                  clearButtonClassName='!tw-hidden'
                  value={
                    bankDetailsFilter.date?.min
                      ? new Date(bankDetailsFilter.date.min)
                      : undefined
                  }
                  locale={language}
                  onChange={(e) => {
                    handleSetBankDetailsFilter("date", {
                      ...bankDetailsFilter.date,
                      min: e.target.value as unknown as string,
                    })
                  }}
                  maxDate={
                    bankDetailsFilter.date?.max
                      ? new Date(bankDetailsFilter.date.max)
                      : undefined
                  }
                  placeholder='dd/mm/aaaa'
                  dateFormat='dd/mm/yy'
                  className='w-full'
                  baseZIndex={2302}
                  showIcon
                />
              </div>
              <div className='tw-py-3 tw-px-3 tw-items-baseline tw-mt-auto tw-mb-0'>
                -
              </div>
              <div className='tw-flex tw-flex-col'>
                <label>
                  {t("bancos.cardsTable.detail.filterMenu.dateLabelUntil")}
                </label>
                <CalendarPrimeReact
                  id='fechaHasta'
                  name='fechaHasta'
                  clearButtonClassName='!tw-hidden'
                  value={
                    bankDetailsFilter.date?.max
                      ? new Date(bankDetailsFilter.date.max)
                      : undefined
                  }
                  locale={language}
                  minDate={
                    bankDetailsFilter.date?.min
                      ? new Date(bankDetailsFilter.date.min)
                      : undefined
                  }
                  onChange={(e) => {
                    handleSetBankDetailsFilter("date", {
                      ...bankDetailsFilter.date,
                      max: e.target.value as unknown as string,
                    })
                  }}
                  placeholder='dd/mm/aaaa'
                  dateFormat='dd/mm/yy'
                  className='w-full'
                  baseZIndex={2302}
                  showIcon
                />
              </div>
            </div>
          </div>
        </Dialog>
      </AnimatePresence>
    </>
  )
}
